import Badge from 'LuxKit/Badge'
import LineEnvelopeIcon from 'LuxKit/Icons/line/LineEnvelopeIcon'
import LineGiftIcon from 'LuxKit/Icons/line/LineGiftIcon'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'
import LineGlobeAltIcon from 'LuxKit/Icons/line/LineGlobeAltIcon'
import LineUserIcon from 'LuxKit/Icons/line/LineUserIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import NavBarListItem from 'components/NavKit/NavBarListItem'
import config from 'constants/config'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import {
  hasIncompleteInformation as getHasIncompleteInformation,
  hasUserPreferencesNotification as getHasUserPreferencesNotification,
  isLEStaffOrAdmin,
} from 'selectors/accountSelectors'
import NavBarListItemDivider from '../NavBarListItemDivider'
import NavBarListItemIcon from '../NavBarListItemIcon'
import AccountMenuCreditListItem from './AccountMenuCreditListItem'
import AccountMenuDrawerHeader from './AccountMenuDrawerHeader'
import AccountMenuLogoutListItem from './AccountMenuLogoutListItem'
import AccountMenuLuxPlusDetailsTile from './AccountMenuLuxPlusDetailsTile'
import AccountMenuMyEscapesListItem from './AccountMenuMyEscapesListItem'
import AccountMenuShopOrdersListItem from './AccountMenuShopOrdersListItem'
import AccountMenuTripPlannerListItem from './AccountMenuTripPlannerListItem'
import { getIsMerchandiseEnabled } from 'selectors/merchandiseSelectors'
import ExperimentControlPanel from 'components/DevTools/ExperimentControlPanel/ExperimentControlPanel'
import ModalContext from 'contexts/ModalContext'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import AccountMenuLoyaltyTiles from './AccountMenuLoyaltyTiles'
import Sheet from 'components/utils/Sheet'
import LineCogIcon from 'LuxKit/Icons/line/LineCogIcon'
import { DevPanelOpenContext } from 'components/App/DevPanel/DevPanelContext'

function AccountMenuDrawer() {
  const intl = useIntl()
  const modalElement = useModalElementContext()
  const handleLinkItemClick = useCallback(() => {
    modalElement.resolve()
  }, [modalElement])
  const isStoreMode = useAppSelector(state => state.system.storeMode)
  const referralProgramEnabled = useAppSelector(state => state.auth.account.referralProgramEnabled)
  const hasIncompleteInformation = useAppSelector(getHasIncompleteInformation)
  const hasUserPreferencesNotification = useAppSelector(getHasUserPreferencesNotification)
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const isStaff = useAppSelector(isLEStaffOrAdmin)
  const canSeeExperimentPanel = isSpoofed || isStaff
  const isMerchandiseEnabled = useAppSelector(getIsMerchandiseEnabled)
  const showModal = useContext(ModalContext)

  const openDevPanel = useContext(DevPanelOpenContext)

  const showExperimentPanel = useCallback(() => {
    showModal(<ExperimentControlPanel />)
  }, [showModal])

  const handleOpenDevPanel = useCallback(() => {
    modalElement.resolve()
    setTimeout(openDevPanel, 100)
  }, [modalElement, openDevPanel])

  return <ModalBase mode="drawer-starboard">
    <AccountMenuDrawerHeader />
    <ModalBody>
      <ModalContent flush>
        <VerticalSpacer gap={20}>
          <Sheet padding="0 20">
            <VerticalSpacer gap={16}>
              <AccountMenuLoyaltyTiles onLinkClick={handleLinkItemClick} />
              <AccountMenuLuxPlusDetailsTile onLinkClick={handleLinkItemClick} />
            </VerticalSpacer>
          </Sheet>
          <VerticalSpacer gap={8}>
            <AccountMenuMyEscapesListItem onClick={handleLinkItemClick} />
            {isMerchandiseEnabled && <AccountMenuShopOrdersListItem onClick={handleLinkItemClick} />}
            {!isStoreMode && <AccountMenuTripPlannerListItem onClick={handleLinkItemClick} />}
            <NavBarListItemDivider />
            <NavBarListItem
              startIcon={<NavBarListItemIcon Icon={LineUserIcon} />}
              title={intl.formatMessage({
                id: 'U80-J3KDXei79Aqe5edq',
                defaultMessage: 'My profile',
                description: 'Navigation item title for account profile page in the navigation bar',
              })}
              to="/account/details"
              onClick={handleLinkItemClick}
              endIcon={hasIncompleteInformation ? <Badge kind="dot" mode="critical" /> : undefined}
            />
            {!isStoreMode && <>
              <NavBarListItem
                startIcon={<NavBarListItemIcon Icon={LineUsersAltIcon} />}
                title={intl.formatMessage({
                  id: 'tj_ykm_9tToLVsrMWQxW',
                  defaultMessage: 'Travel companions',
                  description: 'Navigation item title for travel companions page in the navigation bar',
                })}
                to="/account/travel-companions"
                onClick={handleLinkItemClick}
              />
              {config.USER_PREFERENCES_ENABLED && <NavBarListItem
                startIcon={<NavBarListItemIcon Icon={LineGlobeAltIcon} />}
                title={intl.formatMessage({
                  id: 'Xm5eCVHB_8mMSo809-Xk',
                  defaultMessage: 'Travel preferences',
                  description: 'Navigation item title for travel preferences page in the navigation bar',
                })}
                to="/account/travel-preferences"
                onClick={handleLinkItemClick}
                endIcon={hasUserPreferencesNotification ? <Badge kind="dot" mode="critical" /> : undefined}
              />}
              {config.hasSubscriptions && <NavBarListItem
                startIcon={<NavBarListItemIcon Icon={LineEnvelopeIcon} />}
                title={intl.formatMessage({
                  id: 'OA0AfpHHfMXjUI8j5YcS',
                  defaultMessage: 'Communication preferences',
                  description: 'Navigation item title for communication preferences page',
                })}
                to="/account/communication-preferences"
                onClick={handleLinkItemClick}
              />}
              <AccountMenuCreditListItem onClick={handleLinkItemClick} />
            </>}
            {!isStoreMode && config.ENABLE_VOUCHER_PAGE && <NavBarListItem
              startIcon={<NavBarListItemIcon Icon={LineTicketIcon} />}
              title={intl.formatMessage({
                id: 'cXN48qOaS9ycSYOYoj8A',
                defaultMessage: 'Vouchers',
                description: 'Navigation item title for vouchers page for promotional codes or vouchers',
              })}
              to="/account/vouchers"
              onClick={handleLinkItemClick}
            />}
            {!isStoreMode && config.hasReferralProgram && referralProgramEnabled && <>
              <NavBarListItemDivider />
              <NavBarListItem
                startIcon={<NavBarListItemIcon Icon={LineGiftIcon} />}
                title={intl.formatMessage({
                  id: 'B3lOCQ8ixLEM3YFRZSR6',
                  defaultMessage: 'Refer a friend',
                  description: 'Navigation item title for refer a friend page in the navigation bar',
                })}
                to="/refer"
                onClick={handleLinkItemClick}
              />
            </>}
            {canSeeExperimentPanel && <NavBarListItem
              title={intl.formatMessage({
                id: 'QQdpJbIYUv8XvSYvGDbF',
                defaultMessage: 'Show Experiment Panel',
                description: 'Navigation item title for showing experiment control panel',
              })}
              onClick={showExperimentPanel}
            />}
            {config.ENABLE_DEV_PANEL && <>
              <NavBarListItemDivider />
              <NavBarListItem
                startIcon={<NavBarListItemIcon Icon={LineCogIcon} />}
                title={intl.formatMessage({
                  id: 'z4obgz-lW9LZ2dv6x-1-',
                  defaultMessage: 'Dev Tools',
                  description: 'Navigation item title for opening the customer portal developer tools panel',
                })}
                onClick={handleOpenDevPanel}
              />
            </>}
            <NavBarListItemDivider />
            <AccountMenuLogoutListItem onClick={handleLinkItemClick} />
          </VerticalSpacer>
        </VerticalSpacer>
      </ModalContent>
    </ModalBody>
  </ModalBase>
}

export default AccountMenuDrawer
